import { Translation, useTranslation } from 'react-i18next';
import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
  ReactNode,
  forwardRef,
  useImperativeHandle,
} from 'react';
import cx from 'clsx';
import { QueryClientProvider } from 'react-query';
import { RcFile } from 'antd/es/upload';
import message from 'antd/es/message';
import { $getRoot, PASTE_COMMAND } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import MentionsPlugin from '../plugins/MentionsPlugin';
import { MentionNode } from '../nodes/MentionNode';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';

import { AutoLinkNode } from '@lexical/link';

import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { IconRcCloseIcon } from '@/assets/editor';
import { Loading, Button } from '@/components';
import { devLog, debounce, queryClient } from '@/utils';
import { useSwitch, useTheme, THEMES, useCommentEligibility } from '@/hooks';
import { useCommonImageUpload } from '../hooks';
import { EditorEmojiPicker } from '../components/EditorEmojiPicker';
import { ImageUpload } from '../components/ImageUpload';
import { ContentLengthProgress } from '../components/ContentLengthProgress';

import { SIZE_LIMIT } from '../const';
import { ChangeData, RichTextData } from '../types';
import {
  getImpreciseTextContentFromNode,
  parserRichTextContent,
  advancedProcessRichTextContent,
  getTextUnicodeLength,
  insertTextToEditor,
  getCurrentEditorContent,
  AUTO_LINK_PLUGIN_MATCHER,
} from '../utils';

import styles from './index.module.scss';

export type RichTextEditorHandle = {
  getSubmitContent: () => Promise<RichTextData>;
};

type RichTextEditorProps = {
  onContentChange?: (data: ChangeData) => void;
  initialValue?: string;
  onConfirm?: (data: EditorSubmitContentData) => Promise<void>;
  placeholder?: string;
  onPostSuccess?: () => Promise<void>;
  editorClassName?: string;
  placeholderClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  maxLength?: number;
  disableCard?: boolean;
  isDarkMode?: boolean;
  disableUploadImage?: boolean;
  headerSlot?: ReactNode;
  toolbarSlot?: ReactNode;
  wrapperClassName?: string;
  postBtnClassName?: string;
  toolbar?: boolean;
  onInvalidChange?(isDisabled: boolean): void;
  onImageUploading?(isUploading: boolean): void;
  bodySlot?: ReactNode;
  required?: boolean;
  onImageChange?: (name?: string) => void;
};

const RichTextEditorInner = forwardRef<RichTextEditorHandle, RichTextEditorProps>((props, ref) => {
  const {
    onContentChange,
    placeholder = '',
    editorClassName,
    onConfirm,
    onPostSuccess,
    disableUploadImage = false,
    isDarkMode,
    placeholderClassName,
    toolbar = true,
    autoFocus = true,
    maxLength = 300,
    disabled,
    headerSlot,
    toolbarSlot,
    wrapperClassName,
    onInvalidChange,
    onImageUploading,
    required = true,
    bodySlot,
    postBtnClassName,
    onImageChange,
  } = props;

  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();
  const [selectedFile, setSelectedFile] = useState<{
    file: any;
    id?: string;
  } | null>();
  const { mutateAsync: uploadImage } = useCommonImageUpload('feed');
  const { on: isSubmitting, turnOn: startSubmit, turnOff: stopSubmit } = useSwitch(false);
  const [isEmptyContent, setIsEmptyContent] = useState(!!required);
  const isUploadingRef = useRef(false);
  const { setTheme } = useTheme();
  const [commentEligibility] = useCommentEligibility();
  const [contentLength, setContentLength] = useState(0);

  useImperativeHandle(
    ref,
    () => {
      return {
        // 有副作用，会对当前输入内容排除空行，提交时用
        getSubmitContent: async () => {
          const content = await getCurrentEditorContent(editor);
          const result: RichTextData = { ...content };

          if (selectedFile?.id) {
            result.image_ids = [selectedFile?.id];
          }

          return result;
        },
      };
    },
    [editor, selectedFile?.id],
  );

  useEffect(() => {
    setTheme(isDarkMode ? THEMES.DARK : THEMES.DEFAULT);
  }, [isDarkMode, setTheme]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);

      if (!disabled && autoFocus) {
        editor.focus();
      }
    }
  }, [autoFocus, disabled, editor]);

  const isDisabled = useMemo(
    () =>
      Boolean(
        (isEmptyContent && !selectedFile?.id) ||
          (maxLength && contentLength > maxLength) ||
          disabled,
      ),
    [isEmptyContent, selectedFile?.id, maxLength, contentLength, disabled],
  );

  useEffect(() => {
    onInvalidChange?.(isDisabled);
  }, [isDisabled, onInvalidChange]);

  const parserContent = useCallback(() => {
    const editorState = editor.getEditorState();
    editorState.read(() => {
      const root = $getRoot();
      const contentStr = getImpreciseTextContentFromNode(root);
      if (required) {
        setIsEmptyContent(contentStr.trim().length === 0);
      }
      setContentLength(getTextUnicodeLength(contentStr));

      devLog('parserContent', {
        root,
        contentStr,
        length: contentStr.length,
      });
    });
  }, [editor, required]);

  const debouncedParserContent = useMemo(() => debounce(parserContent, 500), [parserContent]);

  const onChange = useCallback(
    editorState => {
      editorState.read(() => {
        // Read the contents of the EditorState here.
        debouncedParserContent();

        if (onContentChange) {
          const { textContent, mentionsList, hashtagsList, urlsWidthIndices, slashesList } =
            parserRichTextContent(editor);
          onContentChange({
            textContent,
            mentionsList,
            hashtagsList,
            urlsWidthIndices,
            slashesList,
          });
        }
      });
    },
    [debouncedParserContent, editor, onContentChange],
  );

  const clearImage = useCallback(() => {
    setSelectedFile(undefined);
    isUploadingRef.current = false;
    onImageChange?.(undefined);
  }, [onImageChange]);

  const handleBeforeUpload = useCallback(
    (file: RcFile) => {
      onImageUploading?.(true);
      return new Promise<void>(async (resolve, reject) => {
        if (file.size > SIZE_LIMIT) {
          message.error(t('CommentEditor.imageSize'));
          return reject();
        }
        setSelectedFile({ file });
        onImageChange?.(file.name);
        isUploadingRef.current = true;
        const imageRes: any = await uploadImage(file).catch(e => {
          message.warn(e);
          clearImage();
        });
        if (imageRes?.image?.id && isUploadingRef.current) {
          setSelectedFile({ file, id: imageRes?.image?.id });
        }
        isUploadingRef.current = false;
        onImageUploading?.(false);
      });
    },
    [onImageChange, clearImage, onImageUploading, uploadImage, t],
  );

  const selectedFileUrl = useMemo(
    () => (selectedFile?.file ? window.URL.createObjectURL(selectedFile?.file) : ''),
    [selectedFile],
  );

  const clearAllContent = useCallback(() => {
    editor.update(() => {
      $getRoot().clear();
      // setIsBold(false);
    });
    clearImage();
  }, [clearImage, editor]);

  const handleSubmitClick = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    let imageId = '';
    startSubmit();
    if (selectedFile?.file) {
      if (!selectedFile?.id) {
        console.log('uploading image');
        stopSubmit();
        return;
      }
      imageId = selectedFile?.id;
    }
    const editorState = editor.getEditorState();

    editorState.read(async () => {
      const { mentionsList, textContent, urlsWidthIndices } =
        await advancedProcessRichTextContent(editor);

      if (onConfirm) {
        const data: EditorSubmitContentData = {
          content: textContent,
          entities: {
            ...(mentionsList.length ? { mentions: mentionsList } : {}),
            ...(urlsWidthIndices.length ? { url: urlsWidthIndices } : {}),
          },
          permissions: commentEligibility,
        };
        if (imageId) {
          data.image_ids = [imageId];
        }
        console.log('submitContent', data);

        onConfirm(data)
          .then(() => {
            clearAllContent();
            onPostSuccess && onPostSuccess();
          })
          .finally(() => {
            stopSubmit();
          });
      }
    });
  }, [
    commentEligibility,
    isSubmitting,
    startSubmit,
    selectedFile?.file,
    selectedFile?.id,
    editor,
    stopSubmit,
    onConfirm,
    clearAllContent,
    onPostSuccess,
  ]);

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (e: ClipboardEvent) => {
        if (!e.clipboardData || disableUploadImage) return false;
        const items = e.clipboardData.items;
        if (items && items.length) {
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
              const file = items[i].getAsFile();
              if (file) {
                handleBeforeUpload(file as RcFile);
              }
              break;
            }
          }
        }
        return false;
      },
      4,
    );
  }, [disableUploadImage, editor, handleBeforeUpload]);

  const handleEmojiSelect = useCallback(
    (text: string) => {
      if (editor) {
        insertTextToEditor(editor, text);
      }
    },
    [editor],
  );

  return (
    <div className={styles.editor}>
      <div className={cx(styles.editorWrapper, wrapperClassName)}>
        {headerSlot ? headerSlot : null}
        <MentionsPlugin />

        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={cx(styles.editorContainer, editorClassName)}
              autoFocus={autoFocus}
            />
          }
          placeholder={
            <span className={cx(styles.placeholder, placeholderClassName)}>{placeholder}</span>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />

        <OnChangePlugin onChange={onChange} />
        <ClearEditorPlugin />
        <HistoryPlugin />
        <AutoLinkPlugin matchers={AUTO_LINK_PLUGIN_MATCHER} />
        {selectedFileUrl ? (
          <div className={styles.card}>
            {selectedFileUrl ? (
              <div className={styles.previewImageWrapper}>
                <IconRcCloseIcon
                  className={styles.previewCloseIcon}
                  onClick={clearImage}
                  width={32}
                  height={32}
                />
                <img className={styles.previewImage} src={selectedFileUrl} alt="" />
                {selectedFile?.id ? null : <Loading className={styles.imageLoading} />}
              </div>
            ) : null}
          </div>
        ) : null}
        {bodySlot}
        {toolbar ? (
          <div className={styles.toolbar}>
            {toolbarSlot}
            {toolbarSlot ? <div className={styles.divider} /> : null}
            <div className={styles.toolbarOperateButtons}>
              {disableUploadImage ? null : (
                <ImageUpload
                  className={styles.toolbarOperateButton}
                  beforeUpload={handleBeforeUpload}
                />
              )}
              <EditorEmojiPicker onEmojiSelect={handleEmojiSelect} />
            </div>

            <div className={styles.toolbarRight}>
              <ContentLengthProgress currentLength={contentLength} maxLimit={maxLength} />
              <Button
                onClick={handleSubmitClick}
                className={cx(
                  styles.commentPostBtn,
                  isDisabled && styles.disabledButton,
                  postBtnClassName,
                )}
                disabled={isDisabled}
                loading={isSubmitting}
                type="primary">
                <Translation>{t => t('CommentEditor.1hg3k')}</Translation>
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export const RichTextCommentEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (props, ref) => {
    const onError = useCallback(error => {
      console.warn('richtext editor', error);
    }, []);

    const initialEditorConfig = useMemo(
      () => ({
        namespace: 'RichTextEditor',
        theme: {},
        nodes: [AutoLinkNode, MentionNode],
        onError,
      }),
      [onError],
    );

    return (
      <QueryClientProvider client={queryClient}>
        <LexicalComposer initialConfig={initialEditorConfig}>
          <RichTextEditorInner {...props} ref={ref} />
        </LexicalComposer>
      </QueryClientProvider>
    );
  },
);
